import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    IconButton,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    OutlinedInput,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Button,
    CircularProgress,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useHeaderMenu } from './hooks';
import ResponseAlert from 'src/components/Alert/responseAlert';

const SLOTS = [0, 1, 2];

interface HeaderMenuModalProps {
    open: boolean;
    onClose: () => void;
}

const HeaderMenuModal: React.FC<HeaderMenuModalProps> = ({ open, onClose }) => {
    const {
        data: {
            slots,
            availableDefaults,
            enabledDefaults,
            isConfigured,
            isLoading,
            isSaving,
            alert,
        },
        methods: {
            toggleDefault,
            setSlot,
            optionsForSlot,
            save,
            setAlert,
        },
    } = useHeaderMenu();

    const handleSave = async () => {
        await save();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <ResponseAlert
                open={alert.open}
                onClose={() => setAlert({ ...alert, open: false })}
                type={alert.type}
                message={alert.message}
                horizontal="right"
            />
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    Header Menu Management
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {/* Default menu (fixed) */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                Default Menu
                            </Typography>
                            <Box display="flex" gap={2} flexWrap="wrap">
                                {availableDefaults.map((name) => (
                                    <FormControlLabel
                                        key={name}
                                        control={
                                            <Checkbox
                                                checked={enabledDefaults.includes(name)}
                                                onChange={() => toggleDefault(name)}
                                            />
                                        }
                                        label={name}
                                    />
                                ))}
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Alert severity="info" icon={<InfoOutlinedIcon />}>
                                You can choose up to 3 additional menu items
                            </Alert>
                        </Grid>

                        {/* 3 additional menu slots */}
                        {SLOTS.map((i) => (
                            <Grid item xs={12} key={i}>
                                <FormControl fullWidth>
                                    <InputLabel id={`slot-${i}-label`} shrink>{`Header Menu ${i + 1}`}</InputLabel>
                                    <Select
                                        labelId={`slot-${i}-label`}
                                        input={<OutlinedInput notched label={`Header Menu ${i + 1}`} />}
                                        value={slots[i] || ''}
                                        onChange={(e) => setSlot(i, e.target.value)}
                                        displayEmpty
                                        renderValue={(selected) =>
                                            selected
                                                ? (selected as string)
                                                : <em style={{ color: '#9e9e9e' }}>Select Menu Name</em>
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {optionsForSlot(i).map((group) => (
                                            <MenuItem key={group.menu_name} value={group.menu_name}>
                                                {group.menu_name} ({group.item_count} Items)
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={isSaving || isLoading}
                    startIcon={isSaving ? <CircularProgress size={20} /> : null}
                >
                    {isConfigured ? 'Update Menu' : 'Submit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HeaderMenuModal;
